/**
 * VorratsBuch's tables as Excel (docs/18-vorratsbuch.md, Bestandsliste): the template of the
 * Eingang and the stock list of Vorräte, each with the sheet „Erklärungen“ (`inboundGuide`), as
 * KellerLog's (views/Import.tsx). Reading: the first sheet that is no guide, or CSV.
 */
import { inboundGuide, isStockList } from "@lagerlog/core";
import type { Cell } from "@lagerlog/core";
import { readCsv } from "./views/Import.tsx";

const WIDE = ["Bezeichnung", "Grundprodukt", "Abteilung", "Notiz", "Kennung"];
const GUIDE_WIDTHS = [22, 8, 40, 26, 70].map((width) => ({ width }));

/** A workbook of one sheet `rows` (headings bold, first row fixed) and the guide after it. */
export async function writePantryBook(rows: Cell[][], sheet: string, fileName: string) {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const cell = (c: Cell, head: boolean) => (c === null || c === undefined ? null : head ? { value: String(c), fontWeight: "bold" as const } : { value: c });
  const guide = inboundGuide().map((r, i) => r.map((c) => (c === "" ? null : { value: c, wrap: true, ...(i === 0 || (r[1] === "" && r[0]) ? { fontWeight: "bold" as const } : {}) })));
  await writeXlsxFile(
    [
      { data: rows.map((r, i) => r.map((c) => cell(c, i === 0))), sheet, columns: (rows[0] ?? []).map((h) => ({ width: WIDE.includes(String(h)) ? 24 : 14 })), stickyRowsCount: 1 },
      { data: guide, sheet: "Erklärungen", columns: GUIDE_WIDTHS, stickyRowsCount: 1 },
    ] as never,
  ).toFile(fileName);
}

export const isTableFile = (file: File) => /\.(xlsx|csv|txt)$/i.test(file.name);

/** The rows of a table file and whether it is the stock list (its sheet „Bestand“). */
export async function readTable(file: File): Promise<{ rows: Cell[][]; stockList: boolean }> {
  if (/\.(csv|txt)$/i.test(file.name)) return { rows: readCsv(await file.text()), stockList: false };
  const { default: readXlsxFile } = await import("read-excel-file/browser");
  const sheets = await readXlsxFile(file);
  const rows = ((sheets.find((s) => !/erkl/i.test(s.sheet)) ?? sheets[0])?.data ?? []) as Cell[][];
  return { rows, stockList: isStockList(sheets.map((s) => s.sheet)) };
}
